import { createSlice } from "@reduxjs/toolkit"

const extractEntity = (state, entity) => {
  const { potentialFormData } = state
  return potentialFormData[entity]
}

const insertEntity = (state, entity, update) => {
  return {
    ...state,
    potentialFormData: {
      ...state.potentialFormData,
      [entity]: update,
    },
  }
}

const setSelected = importSelected => entity => ({ ...entity, importSelected })

export const initialState = {
  forms: [],
  potentialFormData: {
    entries: [],
    sheets: [],
    sections: [],
  },
  loading: false,
}

const importDataSliceConfig = {
  name: "importData",
  initialState: initialState,
  reducers: {
    setLoading: (state, action) => {
      return {
        ...state,
        loading: action.payload,
      }
    },
    setForms: (state, action) => {
      return {
        ...state,
        forms: action.payload,
      }
    },
    setPotentialFormData: (state, action) => {
      return {
        ...state,
        loading: false,
        potentialFormData: action.payload,
      }
    },
    clearPotentialFormData: (state) => {
      return {
        ...state,
        potentialFormData: initialState.potentialFormData,
      }
    },
    toggleSheets: (state, action) => {
      const { showEntries } = action.payload
      const sheets = extractEntity(state, 'sheets')

      const updateSheets = sheets.map(sheet => {
        return {
          ...sheet,
          showEntries,
        }
      })

      return insertEntity(state, 'sheets', updateSheets)
    },
    toggleSheet: (state, action) => {
      const { id, showEntries } = action.payload
      const sheets = extractEntity(state, 'sheets')

      const updateSheets = sheets.map(sheet => {
        if (sheet.id !== id) return sheet

        return {
          ...sheet,
          showEntries,
        }
      })

      return insertEntity(state, 'sheets', updateSheets)
    },
    toggleImportSelected: (state, action) => {
      const { importSelected } = action.payload
      const toggleSelected = setSelected(importSelected)
      const sections = extractEntity(state, 'sections')
      const sheets = extractEntity(state, 'sheets')
      const entries = extractEntity(state, 'entries')

      return {
        ...state,
        potentialFormData: {
          ...state.potentialFormData,
          sections: sections.map(toggleSelected),
          sheets: sheets.map(toggleSelected),
          entries: entries.map(toggleSelected),
        },
      }
    },
    toggleImportSheet: (state, action) => {
      const { id, section_id, importSelected, multi_section } = action.payload
      const sections = extractEntity(state, 'sections')
      const sheets = extractEntity(state, 'sheets')
      const entries = extractEntity(state, 'entries')
      const updateSheets = sheets.map(sheet => {
        if (sheet.id !== id) return sheet

        return {
          ...sheet,
          importSelected,
        }
      })
      const updateEntries = entries.map(entry => {
        if (entry.sheet_id !== id) return entry

        return {
          ...entry,
          importSelected,
        }
      })
      const updateSections = sections.map(section => {
        if (section.id !== section_id) return section

        return {
          ...section,
          importSelected,
        }
      })

      return {
        ...state,
        potentialFormData: {
          ...state.potentialFormData,
          sections: updateSections,
          sheets: updateSheets,
          entries: updateEntries,
        },
      }
    },
    updatePotentialSheet: (state, action) => {
      const { id } = action.payload
      const sheets = extractEntity(state, 'sheets')
      const updateSheets = sheets.map(sheet => {
        if (sheet.id !== id) return sheet

        return {
          ...sheet,
          ...action.payload,
        }
      })

      return insertEntity(state, 'sheets', updateSheets)
    },
    updatePotentialEntry: (state, action) => {
      const { guid } = action.payload
      const entries = extractEntity(state, 'entries')
      const updateEntries = entries.map(entry => {
        if (entry.guid !== guid) return entry

        return {
          ...entry,
          ...action.payload,
        }
      })

      return insertEntity(state, 'entries', updateEntries)
    },
    removePotentialSheet: (state, action) => {
      const { id } = action.payload
      const sheets = extractEntity(state, 'sheets')
      const updateSheets = sheets.filter(sheet => sheet.id !== id)
      const entries = extractEntity(state, 'entries')
      const updateEntries = entries.filter(entry => entry.sheet_id !== id)

      return insertEntity(
        insertEntity(state, 'entries', updateEntries),
        'sheets',
        updateSheets
      )
    },
    removePotentialEntry: (state, action) => {
      const { guid } = action.payload
      const { potentialFormData } = state
      const { entries } = potentialFormData
      const entryToRemove = entries.find(entry => entry.guid === guid)
      const updateEntries = entries
        .map(entry => {
          if (entry.guid !== guid) return entry

          if (entryToRemove.guid === guid) return null

          if (entryToRemove.sheet_id === entry.sheet_id) {
            return {
              ...entry,
              position:
                entryToRemove.position < entry.position
                  ? entry.position - 1
                  : entry.position,
            }
          }
        })
        .filter(entry => entry !== null)

      return {
        ...state,
        potentialFormData: {
          ...potentialFormData,
          entries: updateEntries,
        },
      }
    },
  },
}

export const importDataSlice = createSlice(importDataSliceConfig)
export const {
  actions: importDataActions,
  reducer: importDataReducer
} = importDataSlice
